"use client";

// Note: Next.js metadata must be in a server component file.
// Metadata is defined in the root layout.tsx file.

import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import HomepageHero from "@/components/homepage/HomepageHero";
import HomepageNearMeAlertsModal from "@/components/homepage/HomepageNearMeAlertsModal";
import HomepageView from "@/components/homepage/HomepageView";
import HomepageSectionSkeleton from "@/components/ui/HomepageSectionSkeleton";
import ItemPreviewModal from "@/components/ui/ItemPreviewModal";
import ItemRequestPreviewModal from "@/components/ui/ItemRequestPreviewModal";
import { useAuth } from "@/contexts/AuthContext";
import { useSystemSettings } from "@/contexts/SystemSettingsContext";
import { HOMEPAGE_COPY } from "@/lib/homepage/copy";
import { recordNearMeFunnelMetric } from "@/lib/nearMeMetricsClient";
import { isGrowthPhaseNotificationDefaultsEnabled } from "@/lib/notifications/growth-phase-notification-defaults";
import {
  markNearMePromptSessionShown,
  readNearMePromptDismissCount,
  readNearMePromptDismissedAt,
  readNearMePromptSessionShown,
  recordNearMePromptDismissed,
  shouldShowNearMePrompt,
} from "@/lib/notifications/near-me-prompt-policy";
import { readNotificationSetupBannerSessionShown } from "@/lib/notifications/notification-setup-banner-policy";
import { maybeClearPostRegistrationSetupGrace } from "@/lib/onboardingEngine/notification-setup-coordination";
import { supabase } from "@/lib/supabase";
import type { Item, ItemRequest } from "@/lib/types";

function HomepageShell() {
  const [isNearMeAlertsModalOpen, setIsNearMeAlertsModalOpen] = useState(false);
  const [nearMeModalProactive, setNearMeModalProactive] = useState(false);
  const proactiveNearMeChecked = useRef(false);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<ItemRequest | null>(
    null,
  );
  const { newListingNearMeNotificationsEnabled } = useSystemSettings();
  const { user, isLoading: authLoading } = useAuth();

  const handleItemClick = useCallback((item: Item) => {
    setSelectedItem(item);
    setSelectedItemId(item.id);
  }, []);

  const handleRequestClick = useCallback((request: ItemRequest) => {
    setSelectedRequest(request);
  }, []);

  const handleCloseItemModal = useCallback(() => {
    setSelectedItemId(null);
    setSelectedItem(null);
  }, []);

  const handleCloseRequestModal = useCallback(() => {
    setSelectedRequest(null);
  }, []);

  const handleNearMeModalNotNow = useCallback(() => {
    recordNearMePromptDismissed();
    void recordNearMeFunnelMetric("near_me_prompt_dismissed", {
      source: "homepage_proactive",
    });
    setNearMeModalProactive(false);
  }, []);

  const openNearMeAlertsModal = useCallback((proactive: boolean) => {
    setNearMeModalProactive(proactive);
    setIsNearMeAlertsModalOpen(true);
  }, []);

  useEffect(() => {
    if (
      proactiveNearMeChecked.current ||
      authLoading ||
      !user?.id ||
      !newListingNearMeNotificationsEnabled ||
      !isGrowthPhaseNotificationDefaultsEnabled()
    ) {
      return;
    }

    let cancelled = false;
    proactiveNearMeChecked.current = true;

    void (async () => {
      const { data: prefs } = await supabase
        .from("notification_preferences")
        .select("notify_new_listings_near_me")
        .eq("user_id", user.id)
        .maybeSingle();

      if (cancelled) return;

      if (readNotificationSetupBannerSessionShown()) {
        return;
      }

      const policy = shouldShowNearMePrompt({
        nearMeOn: Boolean(prefs?.notify_new_listings_near_me),
        dismissedAtIso: readNearMePromptDismissedAt(),
        dismissCount: readNearMePromptDismissCount(),
        sessionPromptShown: readNearMePromptSessionShown(),
      });

      if (policy.show) {
        markNearMePromptSessionShown();
        void recordNearMeFunnelMetric("near_me_prompt_shown", {
          source: "homepage_proactive",
        });
        openNearMeAlertsModal(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    authLoading,
    newListingNearMeNotificationsEnabled,
    openNearMeAlertsModal,
    user?.id,
  ]);

  return (
    <>
      <HomepageView
        onOpenNearMeAlerts={openNearMeAlertsModal}
        onItemClick={handleItemClick}
        onRequestClick={handleRequestClick}
      />

      <HomepageNearMeAlertsModal
        isOpen={isNearMeAlertsModalOpen}
        onClose={() => {
          setIsNearMeAlertsModalOpen(false);
          setNearMeModalProactive(false);
          maybeClearPostRegistrationSetupGrace();
        }}
        onNotNow={nearMeModalProactive ? handleNearMeModalNotNow : undefined}
      />

      <ItemPreviewModal
        itemId={selectedItemId}
        isOpen={selectedItemId !== null}
        onClose={handleCloseItemModal}
        initialItem={selectedItem || undefined}
        surface="homepage"
      />

      <ItemRequestPreviewModal
        request={selectedRequest}
        isOpen={selectedRequest !== null}
        onClose={handleCloseRequestModal}
      />
    </>
  );
}

export default function Home() {
  return (
    <ErrorBoundary>
      <Suspense
        fallback={
          <div className="flex flex-col items-center px-4 py-8 md:px-6 md:py-12 lg:px-8">
            <HomepageHero />
            <div className="w-full max-w-7xl mb-12 mt-4">
              <div className="bg-surface shadow-lg rounded-xl p-4 md:p-6">
                <div className="h-12 bg-tertiary rounded mb-4 animate-pulse" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="h-10 bg-tertiary rounded animate-pulse" />
                  <div className="h-10 bg-tertiary rounded animate-pulse" />
                </div>
              </div>
            </div>
            <HomepageSectionSkeleton title={HOMEPAGE_COPY.feed.title} />
          </div>
        }
      >
        <HomepageShell />
      </Suspense>
    </ErrorBoundary>
  );
}
